import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import omit from 'lodash/omit';
import { useContext, useEffect, useState } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'react-toastify';
import { z } from 'zod';
import { ApiError, ProviderCredentialingPersonal, ProviderService } from '../../../api';
import { Card } from '../../../app/components/Card';
import { FormCheckboxField } from '../../../app/components/FormCheckboxField';
import { FormFeedback } from '../../../app/components/FormFeedback';
import { FormInputMaskField } from '../../../app/components/FormInputMaskField';
import { FormFieldVisibility, FormLabel } from '../../../app/components/FormLabel';
import { FormTextField } from '../../../app/components/FormTextField';
import { ProfilePictureUploader } from '../../../app/components/ProfilePictureUploader';
import { SelectController } from '../../../app/components/SelectController';
import { ServerErrorsAlert } from '../../../app/components/ServerErrorsAlert';
import { UnsavedChangesFormPrompt } from '../../../app/components/UnsavedChangesFormPrompt';
import { FormSubmitContext } from '../../../app/contexts/FormSubmitContext';
import { useBlockInvalidChar } from '../../../app/hooks/block-invalid-char';
import { useDocumentTitle } from '../../../app/hooks/use-document-title';
import { providerQueryKeys } from '../../../app/query-keys-factories/providers';
import { userQueryKeys } from '../../../app/query-keys-factories/user';
import { handleApiError } from '../../../app/utils/errorhandling';
import { generateShowFieldErrors, generateZEnumFromEnum } from '../../../utils/forms';
import { USStates } from '../../../utils/local-data';
import { formatEin } from '../../../utils/string';
import { ProviderProfileCreateOutletContext } from '../../Registration/ProviderProfileCreate';
import { ProviderFormContainer } from './ProviderFormContainer';

const editValidationSchema = z.object({
  profile_picture: z.union([z.null(), z.string().length(0), z.string().url()]).optional(),
  first_name: z.string().min(1),
  middle_name: z.string(),
  last_name: z.string().min(1),
  preferred_name: z.string(),
  prefix_name: z.string(),
  other_names: z.string(),
  business_name: z.string().optional(),
  ein: z
    .union([
      z.null(),
      z.string().length(0),
      z.string().regex(/^\d{2}-\d{7}$/, 'Enter a valid EIN (e.g. 12-3456789)'),
    ])
    .optional(),
  gender: z
    .union([z.null(), z.string().length(0), generateZEnumFromEnum(window.GENDER)])
    .optional(),
  phone_number: z.string().min(10).max(20).nullable(),
  street_address: z.union([z.null(), z.string().length(0), z.string()]),
  city: z.union([z.null(), z.string().length(0), z.string()]),
  state: z.union([z.null(), z.string().length(0), generateZEnumFromEnum(USStates)]).optional(),
  zip_code: z.union([z.null(), z.string().length(5)]),
  text_opt_in: z.boolean(),
});

export const ProviderPersonalInfoForm = () => {
  useDocumentTitle('Profile - Personal Info');
  const { formRef, onSuccess } = useOutletContext<ProviderProfileCreateOutletContext>() ?? {};
  const { isSubmitEnabled } = useContext(FormSubmitContext);
  const [showGlobalServerErrors, setShowGlobalServerErrors] = useState(false);
  const [serverErrors, setServerErrors] = useState<ApiError>();
  const [showServerFieldErrors, setShowServerFieldErrors] = useState<{
    profile_picture?: boolean;
    first_name?: boolean;
    middle_name?: boolean;
    last_name?: boolean;
    other_names?: boolean;
    preferred_name?: boolean;
    gender?: boolean;
    state?: boolean;
    text_opt_in?: boolean;
  }>({});
  const { data: provider } = useQuery({
    queryKey: providerQueryKeys.detailCredentialingPersonal(),
    queryFn: () => ProviderService.providerCurrentCredentialingPersonalRetrieve(),
  });

  const blockInvalidChar = useBlockInvalidChar(/[\d]/);

  const {
    clearErrors,
    control,
    handleSubmit,
    register,
    reset,
    formState: { isDirty, isSubmitSuccessful, errors },
  } = useForm({
    resolver: zodResolver(editValidationSchema),
  });

  useEffect(() => {
    if (provider) {
      const formData = {
        ...provider,
        ein: formatEin(provider.ein),
        text_opt_in: !!provider.texting_number,
      };
      reset(formData);
    }
  }, [provider, reset]);

  const queryClient = useQueryClient();
  const mutationProviderCurrentCredentialingPersonalUpdate = useMutation({
    mutationFn: async (data: ProviderCredentialingPersonal) => {
      await ProviderService.providerCurrentCredentialingPersonalUpdate({ requestBody: data });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(providerQueryKeys.all());
      queryClient.invalidateQueries({
        queryKey: userQueryKeys.detail('current'),
      });
    },
  });

  function handleClearErrorsAlert() {
    setShowGlobalServerErrors(false);
  }

  const handleSubmitClick = () => {
    clearErrors();
    setShowGlobalServerErrors(false);
    setShowServerFieldErrors({});
  };

  const onSubmit = async (data) => {
    try {
      await mutationProviderCurrentCredentialingPersonalUpdate.mutateAsync({
        ...data,
        ein: data.ein ? data.ein.replace(/\D/g, '') : null,
      });
      toast.success('Provider Personal Data Updated Successfully');
      onSuccess?.();
    } catch (errors) {
      if (errors.status === 400) {
        const fieldErrors = omit(errors.body, ['non_field_errors']);
        setServerErrors(errors);
        setShowServerFieldErrors(generateShowFieldErrors(fieldErrors));
        setShowGlobalServerErrors(true);
      }
      handleApiError(errors);
    }
  };

  return (
    <Card className="p-4">
      <h2 className="border-bottom pb-2 mb-4">Basic Info</h2>
      <Form noValidate onSubmit={handleSubmit(onSubmit)} ref={formRef}>
        <Form.Group controlId="profile_picture">
          <FormLabel visibility={FormFieldVisibility.CONNECTED}>Profile Picture</FormLabel>
          <ProfilePictureUploader
            name="profile_picture"
            className="mb-3"
            control={control}
            dest="providers_documents"
            isInvalid={!!errors.profile_picture || !!showServerFieldErrors.profile_picture}
            onChange={() => {
              setShowServerFieldErrors(omit(showServerFieldErrors, ['profile_picture']));
            }}
          />
          <FormFeedback
            clientError={errors.profile_picture}
            serverErrors={
              showServerFieldErrors.profile_picture ? serverErrors?.body?.profile_picture : null
            }
          />
        </Form.Group>
        <Row>
          <Col lg={4}>
            <FormTextField
              name="first_name"
              label="First Name"
              serverErrors={serverErrors}
              showServerFieldErrors={showServerFieldErrors}
              setShowServerFieldErrors={setShowServerFieldErrors}
              errors={errors}
              register={register}
              required
              visibility={FormFieldVisibility.CONNECTED}
              placeholder={'Legal first name'}
            />
          </Col>
          <Col lg={4}>
            <FormTextField
              name="middle_name"
              label="Middle Name"
              serverErrors={serverErrors}
              showServerFieldErrors={showServerFieldErrors}
              setShowServerFieldErrors={setShowServerFieldErrors}
              errors={errors}
              register={register}
              visibility={FormFieldVisibility.CONNECTED}
            />
          </Col>
          <Col lg={4}>
            <FormTextField
              name="last_name"
              label="Last Name"
              serverErrors={serverErrors}
              showServerFieldErrors={showServerFieldErrors}
              setShowServerFieldErrors={setShowServerFieldErrors}
              errors={errors}
              register={register}
              required
              visibility={FormFieldVisibility.CONNECTED}
              placeholder={'Legal last name'}
            />
          </Col>
        </Row>
        <Row>
          <Col lg={4}>
            <FormTextField
              name="preferred_name"
              label="Preferred First Name"
              serverErrors={serverErrors}
              showServerFieldErrors={showServerFieldErrors}
              setShowServerFieldErrors={setShowServerFieldErrors}
              errors={errors}
              register={register}
              visibility={FormFieldVisibility.CONNECTED}
            />
          </Col>
          <Col lg={4}>
            <FormTextField
              name="prefix_name"
              label="Salutation Name"
              serverErrors={serverErrors}
              showServerFieldErrors={showServerFieldErrors}
              setShowServerFieldErrors={setShowServerFieldErrors}
              errors={errors}
              register={register}
              visibility={FormFieldVisibility.CONNECTED}
              placeholder={'E.g. Dr, Mr, Mrs, Ms, Miss'}
            />
          </Col>
          <Col lg={4}>
            <FormTextField
              name="other_names"
              label="Other Names"
              serverErrors={serverErrors}
              showServerFieldErrors={showServerFieldErrors}
              setShowServerFieldErrors={setShowServerFieldErrors}
              errors={errors}
              register={register}
              visibility={FormFieldVisibility.CONNECTED}
              placeholder={'E.g. Maiden Name'}
            />
          </Col>
        </Row>
        <Row>
          <Col lg={6}>
            <FormTextField
              name="business_name"
              label="Business Name"
              serverErrors={serverErrors}
              showServerFieldErrors={showServerFieldErrors}
              setShowServerFieldErrors={setShowServerFieldErrors}
              errors={errors}
              register={register}
              visibility={FormFieldVisibility.CONNECTED}
              placeholder={'Company name or DBA'}
            />
          </Col>
          <Col lg={6}>
            <FormInputMaskField
              name="ein"
              label="EIN"
              mask="__-_______"
              replacement={{ _: /\d/ }}
              placeholder="12-3456789"
              serverErrors={serverErrors}
              showServerFieldErrors={showServerFieldErrors}
              setShowServerFieldErrors={setShowServerFieldErrors}
              errors={errors}
              register={register}
              visibility={FormFieldVisibility.CONNECTED}
            />
          </Col>
        </Row>
        <Row>
          <Col lg={6}>
            <FormInputMaskField
              name="phone_number"
              label="Phone Number"
              mask="(___) ___-____"
              replacement={{ _: /\d/ }}
              placeholder="(123) 456-7890"
              serverErrors={serverErrors}
              showServerFieldErrors={showServerFieldErrors}
              setShowServerFieldErrors={setShowServerFieldErrors}
              errors={errors}
              register={register}
              visibility={FormFieldVisibility.CONNECTED}
            />
          </Col>
          <Col lg={6} className="mb-3">
            <FormLabel visibility={FormFieldVisibility.CONNECTED}>Gender</FormLabel>
            <SelectController
              name="gender"
              control={control}
              options={Object.entries(window.GENDER).map(([value, label]) => ({
                value,
                label,
              }))}
              isClearable={false}
              isMulti={false}
              placeholder=""
              onChange={(newValue) => {
                setShowServerFieldErrors((prev) => omit(prev, ['gender']));
              }}
            />
            <FormFeedback
              clientError={errors.gender}
              serverErrors={showServerFieldErrors.gender ? serverErrors?.body?.gender : null}
            />
          </Col>
        </Row>
        {!provider?.texting_number && (
          <div className="d-flex align-items-start mb-3 tw:p-2 tw:bg-brand-green-3 tw:rounded-lg">
            <small className="text-muted">
              Text <strong>START</strong> to 615-842-1966 to receive notifications from Sendit about
              account activity. Message & data rates may apply. Message frequency depends on account
              activity. Reply HELP for help or STOP to cancel.{' '}
              <a
                href="https://www.senditgigs.com/terms-of-service/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms of Use
              </a>{' '}
              &{' '}
              <a
                href="https://www.senditgigs.com/privacy-policy/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>
            </small>
          </div>
        )}
        <FormTextField
          name="street_address"
          label="Street Address"
          serverErrors={serverErrors}
          showServerFieldErrors={showServerFieldErrors}
          setShowServerFieldErrors={setShowServerFieldErrors}
          errors={errors}
          register={register}
          visibility={FormFieldVisibility.CONNECTED}
        />
        <FormTextField
          name="city"
          label="City"
          serverErrors={serverErrors}
          showServerFieldErrors={showServerFieldErrors}
          setShowServerFieldErrors={setShowServerFieldErrors}
          errors={errors}
          register={register}
          visibility={FormFieldVisibility.CONNECTED}
        />
        <Row>
          <Col xs={6}>
            <FormLabel visibility={FormFieldVisibility.CONNECTED}>State</FormLabel>
            <SelectController
              name="state"
              control={control}
              options={Object.keys(USStates).map((stateCode) => ({
                value: stateCode,
                label: stateCode,
              }))}
              isClearable={true}
              isMulti={false}
              placeholder=""
              onChange={() => {
                setShowServerFieldErrors((prev) => omit(prev, ['state']));
              }}
            />
            <FormFeedback
              clientError={errors.state}
              serverErrors={showServerFieldErrors.state ? serverErrors?.body?.state : null}
            />
          </Col>
          <Col xs={6}>
            <FormTextField
              name="zip_code"
              label="Zip"
              onKeyDown={blockInvalidChar}
              maxLength={5}
              serverErrors={serverErrors}
              showServerFieldErrors={showServerFieldErrors}
              setShowServerFieldErrors={setShowServerFieldErrors}
              errors={errors}
              register={register}
              visibility={FormFieldVisibility.CONNECTED}
            />
          </Col>
        </Row>
        <ServerErrorsAlert
          errors={serverErrors}
          showErrors={showGlobalServerErrors}
          clearErrors={handleClearErrorsAlert}
        />
        <Row>
          <Col md={12}>
            <Button
              className="float-end"
              variant="primary"
              type="submit"
              onClick={() => handleSubmitClick()}
              disabled={
                mutationProviderCurrentCredentialingPersonalUpdate.isLoading || !isSubmitEnabled
              }
            >
              Save Changes
            </Button>
          </Col>
        </Row>
      </Form>
      <UnsavedChangesFormPrompt hasUnsavedChanges={isDirty && !isSubmitSuccessful} />
    </Card>
  );
};

export const ProviderPersonalInfo = () => {
  return (
    <ProviderFormContainer>
      <ProviderPersonalInfoForm />
    </ProviderFormContainer>
  );
};
